import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { api_fetch, api_fetch_throws } from "@/api/client.tsx"
import { toast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Check, Minus } from "lucide-react"
import type { AgentRun } from "@/api/agent_run.ts"
import { auto_applied_kinds, executed_action_kinds, run_was_executed } from "@/api/agent_run.ts"
import type { User } from "@/api"
import { mergeCategoryOptions, useOrgCategoryNames } from "@/deduction/category_options.ts"

/** One promotion-coded split line (mirrors the Rust `AgentPromoSplit`). */
export interface WizardSplit {
  amount: number
  promotion_id: string
  reason_code_id?: string | null
  retailer_name?: string | null
  product_line?: string | null
  execution_date?: string | null
  /** Promo window (date-range orgs) — shown instead of execution_date when set. */
  promotion_start_date?: string | null
  promotion_end_date?: string | null
  reason?: string | null
  /** Display-only, filled server-side: the matched promotion's name. */
  promotion_name?: string | null
  /** Display-only, filled server-side: the chosen reason code's label. */
  reason_code_label?: string | null
}

/** One suggested action from the wizard (mirrors the Rust `AgentAction`). */
export interface WizardAction {
  action: string
  dispute_message?: string | null
  status_value?: string | null
  task_type?: string | null
  task_note?: string | null
  backup_types?: string[] | null
  assign_to_user_id?: string | null
  note_text?: string | null
  category?: string | null
  category_description?: string | null
  task_id?: string | null
  po_number?: string | null
  retailer_name?: string | null
  execution_date?: string | null
  splits?: WizardSplit[] | null
  /** Filled server-side when a deterministic check distrusts the action (e.g. the
   *  match_promotions rate guard). The action starts unchecked and shows this text. */
  review_warning?: string | null
}

function format_usd(amount: number): string {
  if (Number.isNaN(amount)) return "—"
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
}

// Mirror the server's `clamp_words` (execute_categorize in server/src/agent/mod.rs):
// keep the first N whitespace-separated tokens so the preview matches what is saved.
const CATEGORY_DESC_MAX_WORDS = 8
function clamp_words(s: string, max: number): string {
  return s.split(/\s+/).filter(Boolean).slice(0, max).join(" ")
}

// Clamp a categorize action's description to the same word limit the server enforces,
// so the preview and submitted value never diverge from what is persisted.
function clamp_category_description(action: WizardAction): WizardAction {
  if (action.action !== "categorize" || !action.category_description) return action
  return { ...action, category_description: clamp_words(action.category_description, CATEGORY_DESC_MAX_WORDS) }
}

interface WizardDecisionPayload {
  reasoning?: string
  confidence?: number
  actions?: WizardAction[]
}

const ACTION_LABEL: Record<string, string> = {
  categorize: "Categorize",
  update_status: "Update status",
  update_fields: "Update fields",
  assign_task: "Assign task",
  request_backup: "Request backup",
  file_dispute: "File dispute",
  add_note: "Add note",
  complete_task: "Complete task",
  match_promotions: "Match promotions & create splits",
  skip: "Skip",
}

const ACTION_ACCENT: Record<string, string> = {
  categorize: "border-l-teal-400",
  update_status: "border-l-blue-400",
  update_fields: "border-l-cyan-400",
  assign_task: "border-l-purple-400",
  request_backup: "border-l-yellow-400",
  file_dispute: "border-l-green-400",
  add_note: "border-l-gray-400",
  complete_task: "border-l-indigo-400",
  match_promotions: "border-l-fuchsia-400",
  skip: "border-l-gray-200",
}

const STATUS_OPTIONS: [string, string][] = [
  ["disputable", "Disputable"],
  ["validated", "Validated"],
  ["won", "Won"],
]

const TASK_TYPE_OPTIONS: [string, string][] = [
  ["pull-backup", "Pull backup"],
  ["file-dispute", "File dispute"],
  ["validate", "Validate"],
]

// Actions that have no side effect and shouldn't be offered for execution.
const NON_EXECUTABLE = new Set(["skip", "error"])

interface EditableItem {
  key: number
  included: boolean
  action: WizardAction
}

interface WizardActionsEditorProps {
  run: AgentRun
  /** When false, fields are read-only and the run cannot be executed (e.g. already applied). */
  readOnly: boolean
  onExecuted: () => void
  /** Extra control rendered alongside Run / Save in the action button row (e.g. re-run). */
  trailing?: preact.ComponentChildren
}

function parse_actions(run: AgentRun): WizardAction[] {
  const payload = run.action_payload as WizardDecisionPayload | undefined
  const actions = payload?.actions
  if (!Array.isArray(actions)) return []
  return actions
}

function parse_confidence(run: AgentRun): number | null {
  const payload = run.action_payload as WizardDecisionPayload | undefined
  const c = payload?.confidence
  return typeof c === "number" && Number.isFinite(c) ? c : null
}

// Below this confidence, task assignments start unchecked: reviewers deselect
// low-conviction validate tasks far more often than any other correction, so
// creating one should be an opt-in, not an opt-out.
const TASK_CONFIDENCE_THRESHOLD = 0.6

const changed = (x?: string | null, y?: string | null) => (x ?? "") !== (y ?? "")

/** Inline "AI → USER" diff for short scalar fields. */
function inline_diff(label: string, ai?: string | null, user?: string | null): string | null {
  if (!changed(ai, user)) return null
  return `  - ${label}: "${ai ?? ""}" → "${user ?? ""}"`
}

/** Multi-line "AI: … / USER: …" diff for long text fields. */
function block_diff(label: string, ai?: string | null, user?: string | null): string | null {
  if (!changed(ai, user)) return null
  return `  - ${label}:\n      AI:   ${ai ?? "(empty)"}\n      USER: ${user ?? "(empty)"}`
}

/**
 * Build a self-explanatory record of the user's review: which suggested actions
 * they ran/dropped and every field they edited (AI value → user value), plus the
 * exact actions executed. Stored as an annotation so the admin "copy full trace"
 * shows precisely what the user changed before running it.
 */
function build_annotation(
  original: WizardAction[],
  items: EditableItem[],
  run: AgentRun,
  users: User[],
  executed: boolean
): string {
  const user_name = (id?: string | null): string => {
    if (!id) return "(none)"
    const u = users.find(x => x.id === id)
    return u ? `${user_display_name(u)} (${id})` : id
  }

  const included = items.filter(i => i.included && !NON_EXECUTABLE.has(i.action.action))
  const lines: string[] = []
  lines.push(
    executed
      ? `User reviewed the wizard's suggestion and RAN ${included.length}/${original.length} action(s).`
      : `User reviewed the wizard's suggestion and SAVED edits as feedback (${included.length}/${original.length} selected, NOT run).`
  )
  const deselected = items.filter(i => !i.included).map(i => i.action.action)
  lines.push(`Deselected (not run): ${deselected.length ? deselected.join(", ") : "none"}`)

  const edit_blocks: string[] = []
  items.forEach((item, idx) => {
    const orig = original[idx]
    if (!orig) return
    const a = item.action
    const diffs: string[] = []
    if (a.action === "categorize") {
      diffs.push(inline_diff("category", orig.category, a.category) ?? "")
      diffs.push(inline_diff("category_description", orig.category_description, a.category_description) ?? "")
    }
    if (a.action === "update_status") diffs.push(inline_diff("status_value", orig.status_value, a.status_value) ?? "")
    if (a.action === "update_fields") {
      diffs.push(inline_diff("po_number", orig.po_number, a.po_number) ?? "")
      diffs.push(inline_diff("retailer_name", orig.retailer_name, a.retailer_name) ?? "")
      diffs.push(inline_diff("execution_date", orig.execution_date, a.execution_date) ?? "")
    }
    if (a.action === "file_dispute") diffs.push(block_diff("dispute_message", orig.dispute_message, a.dispute_message) ?? "")
    if (a.action === "add_note") diffs.push(block_diff("note_text", orig.note_text, a.note_text) ?? "")
    if (a.action === "assign_task" || a.action === "request_backup") {
      diffs.push(inline_diff("task_type", orig.task_type, a.task_type) ?? "")
      if (changed(a.assign_to_user_id, orig.assign_to_user_id))
        diffs.push(`  - assignee: ${user_name(orig.assign_to_user_id)} → ${user_name(a.assign_to_user_id)}`)
      diffs.push(block_diff("task_note", orig.task_note, a.task_note) ?? "")
    }
    const present = diffs.filter(Boolean)
    if (present.length) edit_blocks.push(`[${a.action}] edits:\n${present.join("\n")}`)
  })

  if (edit_blocks.length) {
    lines.push("", "EDITS (AI value → user value):", ...edit_blocks)
  } else {
    lines.push("", "EDITS: none (ran the wizard's suggestion as-is)")
  }

  const final_actions = included.map(i => i.action)
  lines.push("", `${executed ? "EXECUTED" : "SELECTED"} ACTIONS (JSON):`, JSON.stringify(final_actions, null, 2))

  return lines.join("\n")
}

export function WizardActionsEditor({ run, readOnly, onExecuted, trailing }: WizardActionsEditorProps) {
  const original = useMemo(() => parse_actions(run), [run.id])
  const confidence = parse_confidence(run)
  const low_confidence = confidence !== null && confidence < TASK_CONFIDENCE_THRESHOLD
  // Categorize is applied as soon as BigWiz decides; re-running it only matters once the
  // reviewer changes it, so it starts unselected and selects itself on edit.
  const auto_applied = auto_applied_kinds(run)
  const [items, setItems] = useState<EditableItem[]>(() =>
    original.map((action, key) => ({
      key,
      included:
        !NON_EXECUTABLE.has(action.action) &&
        !(low_confidence && action.action === "assign_task") &&
        !action.review_warning &&
        !auto_applied.includes(action.action),
      action: clamp_category_description({ ...action }),
    }))
  )
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  // Actions this run applied. Set optimistically on execute so the ticks appear
  // immediately, otherwise read from the server's record.
  const [justExecuted, setJustExecuted] = useState<string[] | null>(null)
  const executed_kinds = justExecuted ?? executed_action_kinds(run)

  /** Per-action state, or null when we can't know which actions ran. */
  function executed_state(action: WizardAction): ExecutedState {
    if (!executed_kinds || (!run_was_executed(run) && !done)) return null
    if (NON_EXECUTABLE.has(action.action)) return null
    return executed_kinds.includes(action.action) ? "ran" : "not_run"
  }

  const { data: users } = useSWR<User[]>("/users", () => api_fetch_throws<User[]>("/users", { use_cache: true }))
  const orgCategoryNames = useOrgCategoryNames()

  function update_action(key: number, patch: Partial<WizardAction>) {
    setItems(prev =>
      prev.map(it =>
        it.key === key
          ? // editing an auto-applied action is what makes it worth running again
            { ...it, included: it.included || auto_applied.includes(it.action.action), action: { ...it.action, ...patch } }
          : it
      )
    )
  }
  function toggle_included(key: number, included: boolean) {
    setItems(prev => prev.map(it => (it.key === key ? { ...it, included } : it)))
  }

  const selected = items.filter(it => it.included && !NON_EXECUTABLE.has(it.action.action))

  async function run_selected() {
    if (selected.length === 0) {
      toast({ title: "Nothing selected", description: "Select at least one action to run.", variant: "destructive" })
      return
    }
    setBusy(true)
    try {
      const annotation = build_annotation(original, items, run, users ?? [], true)
      const res = await api_fetch<{ executed: boolean; results: { summary?: string; kind?: string }[] }>(`/agent/runs/${run.id}/execute`, {
        method: "POST",
        body: { actions: selected.map(it => clamp_category_description(it.action)), annotation },
      })
      if (res.ok) {
        const results = res.value.data.results ?? []
        const summaries = results.map(r => r.summary).filter(Boolean)
        // Tick exactly what the server reports as run (gating can drop an action
        // we submitted), falling back to the submitted set if it reports no kinds.
        const ran_kinds = results.map(r => r.kind).filter((k): k is string => !!k)
        const ran = ran_kinds.length ? ran_kinds : selected.map(it => it.action.action)
        // An auto-applied action ran even when the reviewer left it unselected.
        setJustExecuted([...new Set([...auto_applied, ...ran])])
        toast({ title: "Actions executed", description: summaries.join(" ") || "Applied selected actions." })
        setDone(true)
        onExecuted()
      } else {
        toast({ title: "Execute failed", description: res.error?.message ?? "Unknown error", variant: "destructive" })
      }
    } finally {
      setBusy(false)
    }
  }

  async function save_feedback() {
    setBusy(true)
    try {
      const annotation = build_annotation(original, items, run, users ?? [], false)
      const res = await api_fetch(`/agent/runs/${run.id}/annotation`, {
        method: "POST",
        body: { trace_section: "action_payload", body: annotation },
      })
      if (res.ok) toast({ title: "Feedback saved", description: "Your edits were recorded to improve the wizard." })
      else toast({ title: "Save failed", description: res.error?.message ?? "Unknown error", variant: "destructive" })
    } finally {
      setBusy(false)
    }
  }

  if (original.length === 0) {
    return <p class="text-xs text-gray-500">No structured actions to show.</p>
  }

  const disabled = readOnly || done

  return (
    <div class="space-y-2">
      {confidence !== null && (
        <p class={`text-[11px] ${low_confidence ? "text-amber-700" : "text-gray-500"}`}>
          Wizard confidence: {Math.round(confidence * 100)}%
          {low_confidence && " — task assignment unchecked by default"}
        </p>
      )}
      {items.map(item => (
        <ActionCard
          key={item.key}
          item={item}
          run={run}
          users={users ?? []}
          categoryNames={orgCategoryNames}
          disabled={disabled}
          onToggle={included => toggle_included(item.key, included)}
          onChange={patch => update_action(item.key, patch)}
          executedState={executed_state(item.action)}
          autoApplied={!done && auto_applied.includes(item.action.action)}
        />
      ))}

      {!readOnly && !done && (
        <div class="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" disabled={busy || selected.length === 0} onClick={() => void run_selected()}>
            {busy ? "Running…" : `Run ${selected.length} selected action${selected.length !== 1 ? "s" : ""}`}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void save_feedback()}>
            Save edits as feedback
          </Button>
          {trailing}
          <span class="text-[11px] text-amber-700">The deduction may have changed since this run.</span>
        </div>
      )}
      {done && <p class="text-xs text-emerald-700">✓ Selected actions applied.</p>}
    </div>
  )
}

/** Per-action execution state: `null` when the run's executed set is unknown. */
type ExecutedState = "ran" | "not_run" | null

interface ActionCardProps {
  item: EditableItem
  run: AgentRun
  users: User[]
  /** The org's category names (from GET /categories) for the categorize picker. */
  categoryNames: string[]
  disabled: boolean
  onToggle: (included: boolean) => void
  onChange: (patch: Partial<WizardAction>) => void
  executedState: ExecutedState
  /** Already applied by BigWiz before review; still editable, re-run when changed. */
  autoApplied: boolean
}

function ActionCard({ item, run, users, categoryNames, disabled, onToggle, onChange, executedState, autoApplied }: ActionCardProps) {
  const a = item.action
  const label = ACTION_LABEL[a.action] ?? a.action
  const accent = ACTION_ACCENT[a.action] ?? "border-l-gray-300"
  const selectable = !NON_EXECUTABLE.has(a.action)
  // Only an action we know ran gets a tick; one we know did NOT run is muted.
  // When the executed set is unknown (legacy run), neither is shown.
  const ran = executedState === "ran"
  const not_run = executedState === "not_run"

  return (
    <div class={`rounded border border-l-4 ${accent} bg-white p-2.5 ${not_run ? "opacity-60" : ""}`}>
      <div class="flex items-center gap-2">
        {/* Once a run has executed, the checkbox is meaningless — it would show
            this action "selected" even where the reviewer deselected it. Show
            the outcome instead: a tick for what ran, a dash for what didn't. */}
        {ran ? (
          <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Executed" />
        ) : not_run ? (
          <Minus className="h-4 w-4 shrink-0 text-gray-400" aria-label="Not run" />
        ) : (
          selectable && <Checkbox checked={item.included} disabled={disabled} onCheckedChange={(v: boolean) => onToggle(!!v)} />
        )}
        <span class="text-sm font-medium text-gray-800">{label}</span>
        {ran && <span class="text-[11px] font-medium text-emerald-700">Executed</span>}
        {not_run && <span class="text-[11px] text-gray-500">Not run</span>}
        {autoApplied && !ran && !not_run && (
          <span class="text-[11px] font-medium text-emerald-700">
            Applied automatically{item.included ? " · your edit will re-file it" : " · edit to change"}
          </span>
        )}
      </div>
      <div class="mt-2 space-y-2">
        {a.action === "categorize" && (
          <FromToRow from={`${run.category_name}${run.category_description ? ` — ${run.category_description}` : ""}`}>
            <div class="flex flex-col gap-1.5 sm:flex-row">
              <EditSelect
                value={a.category ?? ""}
                disabled={disabled}
                options={mergeCategoryOptions(categoryNames, a.category ? [a.category] : []).map(c => [c, c] as [string, string])}
                placeholder="Select category"
                onChange={v => onChange({ category: v })}
              />
              <Input
                class="text-sm"
                placeholder="Description (≤8 words)"
                value={a.category_description ?? ""}
                disabled={disabled}
                onInput={(e: Event) => onChange({ category_description: (e.target as HTMLInputElement).value })}
              />
            </div>
          </FromToRow>
        )}

        {a.action === "update_status" && (
          <FromToRow from={run.status_value.replaceAll("_", " ")}>
            <EditSelect
              value={a.status_value ?? ""}
              disabled={disabled}
              options={STATUS_OPTIONS}
              placeholder="Select status"
              onChange={v => onChange({ status_value: v })}
            />
          </FromToRow>
        )}

        {(a.action === "assign_task" || a.action === "request_backup") && (
          <div class="space-y-2">
            <FieldLabel text="Assignee">
              <EditSelect
                value={a.assign_to_user_id ?? ""}
                disabled={disabled}
                options={users.map(u => [u.id, user_display_name(u)] as [string, string])}
                placeholder="Select team member"
                onChange={v => onChange({ assign_to_user_id: v })}
              />
            </FieldLabel>
            <FieldLabel text="Task type">
              <EditSelect
                value={a.task_type ?? "pull-backup"}
                disabled={disabled}
                options={TASK_TYPE_OPTIONS}
                placeholder="Task type"
                onChange={v => onChange({ task_type: v })}
              />
            </FieldLabel>
            {a.backup_types && a.backup_types.length > 0 && (
              <p class="text-xs text-gray-500">Backup: {a.backup_types.join(", ")}</p>
            )}
            <FieldLabel text="Note to assignee">
              <Textarea
                class="text-sm"
                rows={3}
                value={a.task_note ?? ""}
                disabled={disabled}
                onInput={(e: Event) => onChange({ task_note: (e.target as HTMLTextAreaElement).value })}
              />
            </FieldLabel>
          </div>
        )}

        {a.action === "file_dispute" && (
          <FieldLabel text="Dispute message (sent to the distributor)">
            <Textarea
              class="text-sm"
              rows={3}
              value={a.dispute_message ?? ""}
              disabled={disabled}
              onInput={(e: Event) => onChange({ dispute_message: (e.target as HTMLTextAreaElement).value })}
            />
            <p class="text-xs text-gray-500">
              Attaches every file on the deduction: uploaded documents, backup and check.
            </p>
          </FieldLabel>
        )}

        {a.action === "add_note" && (
          <FieldLabel text="Internal note">
            <Textarea
              class="text-sm"
              rows={2}
              value={a.note_text ?? ""}
              disabled={disabled}
              onInput={(e: Event) => onChange({ note_text: (e.target as HTMLTextAreaElement).value })}
            />
          </FieldLabel>
        )}

        {a.action === "update_fields" && (
          <div class="space-y-2">
            <FieldLabel text="PO number">
              <Input
                class="text-sm"
                placeholder="PO number"
                value={a.po_number ?? ""}
                disabled={disabled}
                onInput={(e: Event) => onChange({ po_number: (e.target as HTMLInputElement).value })}
              />
            </FieldLabel>
            <FieldLabel text="Retailer">
              <Input
                class="text-sm"
                placeholder="Retailer"
                value={a.retailer_name ?? ""}
                disabled={disabled}
                onInput={(e: Event) => onChange({ retailer_name: (e.target as HTMLInputElement).value })}
              />
            </FieldLabel>
            <FieldLabel text="Execution date (YYYY-MM-DD)">
              <Input
                class="text-sm"
                placeholder="YYYY-MM-DD"
                value={a.execution_date ?? ""}
                disabled={disabled}
                onInput={(e: Event) => onChange({ execution_date: (e.target as HTMLInputElement).value })}
              />
            </FieldLabel>
            <p class="text-[11px] text-gray-500">Only fields that are currently empty on the deduction are filled.</p>
          </div>
        )}

        {a.action === "complete_task" && (
          <p class="text-xs text-gray-600">Marks task <span class="font-mono">{(a.task_id ?? "").slice(0, 8)}…</span> completed.</p>
        )}

        {a.review_warning && (
          <p class="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
            {a.review_warning} <span class="text-amber-700">Unchecked by default.</span>
          </p>
        )}

        {a.action === "match_promotions" && <MatchPromotionsDetail splits={a.splits ?? []} />}

        {a.action === "skip" && <p class="text-xs text-gray-500">No action.</p>}
      </div>
    </div>
  )
}

/**
 * Read-only summary of the promotion(s) the wizard matched, with the split
 * amount, reason code (GL account) and evidence per line — so the reviewer can
 * see *which* promotion is being coded before running the action.
 */
function MatchPromotionsDetail({ splits }: { splits: WizardSplit[] }) {
  if (splits.length === 0) {
    return <p class="text-xs text-gray-500">No promotion splits provided.</p>
  }
  const total = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0)
  return (
    <div class="space-y-1.5">
      {splits.map((s, i) => (
        <div key={s.promotion_id + i} class="rounded border border-gray-100 bg-gray-50 p-2">
          <div class="flex items-baseline justify-between gap-2">
            {/* A missing name means the server could not resolve the id to a promotion,
                so this split will be rejected on Execute — say so rather than showing a
                truncated id that reads like any other. */}
            <span class="text-sm font-medium text-gray-800">
              {s.promotion_name || (
                <span class="text-red-600">
                  Unknown promotion <span class="font-mono text-xs">{s.promotion_id.slice(0, 8)}…</span>
                </span>
              )}
            </span>
            <span class="shrink-0 text-sm font-semibold tabular-nums text-gray-700">{format_usd(Number(s.amount) || 0)}</span>
          </div>
          <div class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
            {s.reason_code_label && (
              <span>
                Reason code: <span class="text-gray-700">{s.reason_code_label}</span>
              </span>
            )}
            {s.retailer_name && <span>· {s.retailer_name}</span>}
            {s.promotion_start_date || s.promotion_end_date ? (
              <span>
                · {s.promotion_start_date ?? "?"} – {s.promotion_end_date ?? "?"}
              </span>
            ) : (
              s.execution_date && <span>· {s.execution_date}</span>
            )}
          </div>
          {s.reason && <p class="mt-0.5 text-xs italic text-gray-500">{s.reason}</p>}
        </div>
      ))}
      {splits.length > 1 && (
        <p class="text-right text-xs text-gray-500">
          Total: <span class="font-semibold tabular-nums text-gray-700">{format_usd(total)}</span>
        </p>
      )}
    </div>
  )
}

function FromToRow({ from, children }: { from: string; children: preact.ComponentChildren }) {
  return (
    <div class="flex flex-col gap-1.5 sm:flex-row sm:items-center">
      <span class="text-xs text-gray-500 shrink-0">
        From <span class="font-medium text-gray-700">{from || "—"}</span> →
      </span>
      <div class="flex-1">{children}</div>
    </div>
  )
}

function FieldLabel({ text, children }: { text: string; children: preact.ComponentChildren }) {
  return (
    <div>
      <Label class="text-xs text-gray-500">{text}</Label>
      <div class="mt-1">{children}</div>
    </div>
  )
}

function EditSelect({
  value,
  options,
  placeholder,
  disabled,
  onChange,
}: {
  value: string
  options: [string, string][]
  placeholder: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger class="w-full text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(([v, label]) => (
          <SelectItem key={v} value={v}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function user_display_name(u: User): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim()
  return name || u.email || u.id
}
